import { useEffect, useRef, useCallback } from "react";

export function useTabScopedIdleLogout({
    logoutFn,
    idleMs = 10 * 60 * 1000,
    countNetworkActivityInThisTab = false,
}) {
    const timerRef = useRef(null);
    const listenersAttachedRef = useRef(false);
    const originalFetchRef = useRef(null);
    const originalXHROpenRef = useRef(null);

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const startTimer = useCallback(() => {
        clearTimer();
        timerRef.current = setTimeout(() => {
            logoutFn?.();
        }, idleMs);
    }, [idleMs, logoutFn, clearTimer]);

    const resetHandler = useCallback(() => {
        if (document.visibilityState === "visible") {
            startTimer();
        }
    }, [startTimer]);

    useEffect(() => {
        const events = ["mousedown", "keydown", "scroll"];

        function attachListeners() {
            if (listenersAttachedRef.current) return;
            events.forEach((ev) => window.addEventListener(ev, resetHandler, true));
            listenersAttachedRef.current = true;
        }

        function detachListeners() {
            if (!listenersAttachedRef.current) return;
            events.forEach((ev) => window.removeEventListener(ev, resetHandler, true));
            listenersAttachedRef.current = false;
        }

        function onVisibilityChange() {
            if (document.visibilityState === "visible") {
                attachListeners();
                startTimer();
            } else {
                detachListeners();
                // timer keeps running while tab is hidden
            }
        }

        if (document.visibilityState === "visible") {
            attachListeners();
            startTimer();
        } else {
            startTimer();
        }

        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            detachListeners();
            document.removeEventListener("visibilitychange", onVisibilityChange);
            clearTimer();
        };
    }, [resetHandler, startTimer, clearTimer]);

    // Optional: count network activity for this tab
    useEffect(() => {
        if (!countNetworkActivityInThisTab) return;

        if (window.fetch) {
            originalFetchRef.current = window.fetch;
            window.fetch = async function (...args) {
                if (document.visibilityState === "visible") startTimer();
                try {
                    return await originalFetchRef.current.apply(this, args);
                } finally {
                    if (document.visibilityState === "visible") startTimer();
                }
            };
        }

        const originalOpen = XMLHttpRequest.prototype.open;
        originalXHROpenRef.current = originalOpen;
        XMLHttpRequest.prototype.open = function (...args) {
            this.addEventListener("readystatechange", () => {
                if (this.readyState === 1 && document.visibilityState === "visible") {
                    startTimer();
                }
            });
            return originalOpen.apply(this, args);
        };

        return () => {
            if (originalFetchRef.current) window.fetch = originalFetchRef.current;
            if (originalXHROpenRef.current) XMLHttpRequest.prototype.open = originalXHROpenRef.current;
        };
    }, [countNetworkActivityInThisTab, startTimer]);
}
